import React, { useEffect, useState } from 'react';
import { useImmer } from 'use-immer';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import { collect } from 'collect.js';
import { Button, Drawer, Select, Table, Tag } from 'antd';
import { ReloadOutlined } from '@ant-design/icons';
import moment from 'moment-timezone';
import useApiClient from '../../../hooks/api/useApiClient';
import { TeamReportFormat, TeamReportType } from '../../../services/exports/Constants';
import useHelpers from '../../../hooks/context/useHelpers';
import DateRangePicker, { filterFactory, filter_this_month, variant_dropdown } from '../../filters/DateRangePicker';
import ManagerSelect from '../../form/business/ManagerSelect';
import usePermissions, { ManagerPermission, ManagerRole } from '../../../hooks/auth/usePermissions';
import useDateRangeFilter from '../../../hooks/utility/useDateRangeFilter';

const MANAGER_STATUS_ALL = 'all';
const MANAGER_STATUS_ACTIVE = 'active';
const MANAGER_STATUS_INACTIVE = 'inactive';

export default function GoLiveConversionReport() {
  const { t } = useTranslation(undefined, { keyPrefix: 'Components:Team:Reports:GoLiveConversionReport' });
  const { t: tRoles } = useTranslation(undefined, { keyPrefix: 'Shared:Roles' });

  const { TeamReportsManager } = useApiClient();

  const { profile } = useSelector((state) => state.initialData);

  const { formatDateTime } = useHelpers();
  const { can, hasAnyRole } = usePermissions();
  const hasManagerSelect = can(ManagerPermission.FullAccessSignedLiveReports);
  const isAdmin = hasAnyRole([ManagerRole.SuperAdmin, ManagerRole.Admin]);

  const [loading, setLoading] = useState(true);
  const [selectedManagers, setSelectedManagers] = useState(hasManagerSelect ? [] : [profile.id]);
  const [dateRangeFilter, setDateRangeFilter] = useDateRangeFilter(filterFactory(filter_this_month));
  const [managerStatusFilter, setManagerStatusFilter] = useState(MANAGER_STATUS_ACTIVE);
  const [report, setReport] = useState(null);
  const [drawer, setDrawer] = useImmer({});

  useEffect(() => {
    selectedManagers?.length > 0 && loadReport();
  }, [selectedManagers, dateRangeFilter]);

  const onLoadManagers = ({ data, setManagers }) => {
    const filteredManagers = collect(data)
      .filter((item) =>
        collect(item.roles)
          .intersect([
            ManagerRole.SuperAdmin,
            ManagerRole.Admin,
            ManagerRole.Reseller,
            ManagerRole.SalesManager,
            ManagerRole.SalesLeadership,
            ManagerRole.OnboardingManager,
          ])
          .isNotEmpty(),
      )
      .toArray();

    setManagers(filteredManagers);
    setSelectedManagers(
      collect(filteredManagers)
        .filter((item) =>
          collect(item.roles)
            .intersect([ManagerRole.Reseller, ManagerRole.SalesManager, ManagerRole.SalesLeadership])
            .isNotEmpty(),
        )
        .pluck('id')
        .toArray(),
    );
    setLoading(false);
  };

  const loadReport = async () => {
    setLoading(true);
    const { success, data } = await TeamReportsManager.create({
      format: TeamReportFormat.JSON,
      type: TeamReportType.SignedLive,
      start: dateRangeFilter?.start_date,
      end: dateRangeFilter?.end_date,
      managers: selectedManagers,
    });
    setLoading(false);

    if (!success) {
      toast.error(t('toasts.failed_to_load_data'));

      return null;
    }

    setReport(data);
  };

  const calculateDiffInDays = (signedAt, liveFrom) => {
    const signedAtDate = moment(signedAt).startOf('day');
    const liveFromDate = moment(liveFrom).startOf('day');

    return liveFromDate.diff(signedAtDate, 'days');
  };

  const openDrawer = (columnLabel, managerName, places) => {
    const count = (places || []).length;
    setDrawer((draft) => {
      draft.title = t('drawer.title', { column: columnLabel, managerName });
      draft.sub = t('drawer.subtitle', { count });
      draft.places = places || [];
    });
  };

  const renderNumCell = (places, label, managerName) => {
    const count = places?.length ?? 0;
    if (!places) return '-';
    if (count === 0) return count;

    return (
      <span
        className="cursor-pointer underline decoration-gray-300 underline-offset-2"
        onClick={() => openDrawer(label, managerName, places)}
      >
        {count}
      </span>
    );
  };

  const renderLiveCell = (places, managerName) => {
    const count = places?.length ?? 0;
    if (count === 0) {
      return <Tag color="success">{count}</Tag>;
    }

    return (
      <span className="cursor-pointer" onClick={() => openDrawer(t('fields.live'), managerName, places)}>
        <Tag color="success">{count}</Tag>
      </span>
    );
  };

  const currentYear = new Date().getFullYear();

  const columns = [
    {
      title: t('column_groups.current_period'),
      children: [
        {
          title: t('fields.name'),
          key: 'name',
          sorter: (a, b) => (a.manager.name || a.manager.email).localeCompare(b.manager.name || b.manager.email),
          render: (_, item) => (item.manager.name ? item.manager.name : item.manager.email),
        },
        {
          title: t('fields.signed'),
          key: 'signed',
          sorter: (a, b) => (a.places_signed?.length ?? 0) - (b.places_signed?.length ?? 0),
          render: (_, item) =>
            renderNumCell(item.places_signed, t('fields.signed'), item.manager.name || item.manager.email),
        },
        {
          title: t('fields.live'),
          key: 'live',
          sorter: (a, b) => (a.places_enabled?.length ?? 0) - (b.places_enabled?.length ?? 0),
          render: (_, item) => renderLiveCell(item.places_enabled, item.manager.name || item.manager.email),
        },
        {
          title: t('fields.going_live_avg'),
          key: 'going_live_avg',
          sorter: (a, b) => {
            const calcAvg = (items) => {
              const liveItems = (items || []).filter((c) => c.live_from);
              if (!liveItems.length) return -Infinity;

              return (
                collect(liveItems)
                  .map((c) => calculateDiffInDays(c.signed_at, c.live_from))
                  .sum() / liveItems.length
              );
            };

            return calcAvg(a.places_enabled) - calcAvg(b.places_enabled);
          },
          render: (_, item) => {
            const liveItems = (item.places_enabled || []).filter((c) => c.live_from);
            if (!liveItems.length) return '-';
            const sum = collect(liveItems)
              .map((company) => calculateDiffInDays(company.signed_at, company.live_from))
              .sum();

            return parseFloat((sum / liveItems.length).toFixed(2));
          },
        },
      ],
    },
    {
      title: String(currentYear),
      children: [
        {
          title: t('fields.live'),
          key: 'current_year_live',
          sorter: (a, b) => (a.current_year.places_enabled?.length ?? 0) - (b.current_year.places_enabled?.length ?? 0),
          render: (_, item) =>
            renderLiveCell(item.current_year.places_enabled, item.manager.name || item.manager.email),
        },
        {
          title: t('fields.churned'),
          key: 'churned',
          sorter: (a, b) => (a.current_year.places_churned?.length ?? 0) - (b.current_year.places_churned?.length ?? 0),
          render: (_, item) =>
            renderNumCell(
              item.current_year.places_churned,
              t('fields.churned'),
              item.manager.name || item.manager.email,
            ),
        },
        {
          title: t('fields.not_live'),
          key: 'not_live',
          sorter: (a, b) =>
            (a.current_year.places_not_live?.length ?? 0) - (b.current_year.places_not_live?.length ?? 0),
          render: (_, item) =>
            renderNumCell(
              item.current_year.places_not_live,
              t('fields.not_live'),
              item.manager.name || item.manager.email,
            ),
        },
        {
          title: t('fields.live_ratio'),
          key: 'live_ratio',
          sorter: (a, b) => {
            const ratio = (item) =>
              (item.current_year.places_signed?.length || 0) > 0
                ? (item.current_year.places_enabled?.length || 0) / item.current_year.places_signed.length
                : -Infinity;

            return ratio(a) - ratio(b);
          },
          render: (_, item) => {
            const signed = item.current_year.places_signed?.length ?? 0;
            const live = item.current_year.places_enabled?.length ?? 0;
            if (signed === 0) return '-';
            const pct = Math.round((live / signed) * 100);

            return (
              <span className="inline-flex items-center gap-1 tabular-nums">
                <span>
                  {live}/{signed}
                </span>
                <span className="text-gray-300">·</span>
                <span>{pct}%</span>
              </span>
            );
          },
        },
        {
          title: t('fields.avg_mrr'),
          key: 'avg_mrr',
          sorter: (a, b) =>
            (a.current_year?.avg_mrr?.amount ?? -Infinity) - (b.current_year?.avg_mrr?.amount ?? -Infinity),
          render: (_, item) => item?.current_year?.avg_mrr?.formatted ?? '-',
        },
      ],
    },
  ];

  const renderDetails = (places) => (
    <Table
      columns={[
        {
          title: t('fields.name'),
          key: 'company_name',
          dataIndex: 'name',
        },
        {
          title: t('fields.signed'),
          key: 'signed_at',
          dataIndex: 'signed_at',
          render: (_, item) => formatDateTime(item.signed_at),
        },
        {
          title: t('fields.live'),
          key: 'live_from',
          dataIndex: 'live_from',
          render: (_, item) => (item.live_from ? formatDateTime(item.live_from) : '-'),
        },
      ]}
      dataSource={places}
      rowKey={(row) => `company-${row.id}`}
      pagination={false}
    />
  );

  const managerStatusOptions = [
    { label: t('manager_status_filters.active'), value: MANAGER_STATUS_ACTIVE },
    { label: t('manager_status_filters.inactive'), value: MANAGER_STATUS_INACTIVE },
    { label: t('manager_status_filters.all'), value: MANAGER_STATUS_ALL },
  ];

  return (
    <>
      <div className="flex justify-between">
        <h2>{t('title')}</h2>
      </div>

      <div className="mt-4 bg-background-inkWhite-white_0 shadow rounded-lg">
        <div className="flex flex-col lg:flex-row justify-between items-center gap-4 p-md border-b border-brand-inkGrey-grey_2">
          <div className="flex flex-wrap items-start gap-3">
            <DateRangePicker
              variant={variant_dropdown}
              defaultFilter={filter_this_month}
              initialValue={dateRangeFilter}
              onChange={setDateRangeFilter}
              excludeFilters={isAdmin ? [] : ['all']}
              hideCustomRange={!isAdmin}
            />
            {hasManagerSelect && (
              <>
                {isAdmin && (
                  <Select
                    value={managerStatusFilter}
                    onChange={setManagerStatusFilter}
                    options={managerStatusOptions}
                    className="min-w-[120px]"
                  />
                )}
                <ManagerSelect
                  status={
                    {
                      [MANAGER_STATUS_ACTIVE]: true,
                      [MANAGER_STATUS_INACTIVE]: false,
                      [MANAGER_STATUS_ALL]: null,
                    }[managerStatusFilter]
                  }
                  hidden={!hasManagerSelect}
                  value={selectedManagers}
                  isMulti
                  onLoad={onLoadManagers}
                  formatOptions={(managers) =>
                    collect(managers)
                      .groupBy((item) => item.roles[0])
                      .map((managers, role) => ({
                        label: tRoles(role),
                        title: tRoles(role),
                        options: managers.map((item) => ({ label: item.name, value: item.id })).toArray(),
                      }))
                      .toArray()
                  }
                  onChange={(value) => setSelectedManagers(value)}
                />
              </>
            )}
          </div>
          <div className="flex items-center justify-end">
            <Button size="large" icon={<ReloadOutlined />} onClick={() => loadReport()} />
          </div>
        </div>
        <Table
          bordered
          dataSource={report}
          columns={columns}
          pagination={false}
          loading={loading}
          className="rounded-xl shadow-card bg-white overflow-hidden"
          rowKey={(item) => item.manager.id}
        />
      </div>

      <Drawer
        title={
          <>
            <div>{drawer.title}</div>
            {drawer.sub && <div className="text-xs font-normal text-gray-400 mt-0.5">{drawer.sub}</div>}
          </>
        }
        open={!!Object.keys(drawer).length}
        onClose={() => setDrawer(() => ({}))}
        size={480}
      >
        {renderDetails(drawer.places)}
      </Drawer>
    </>
  );
}
